import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * NeonButton — pill-shaped button with optional animated neon glow line.
 *
 * Added under SCRUM-728 for the System Status Overview lower panel on
 * the dymaxion-portal landing page (AI-OS Control Panel + System
 * Health Monitoring CTAs). Distinct from the existing @dymaxion/ui
 * `Button` primitive (shadcn-style with rectangular variants) — neon
 * is the marketing/CTA pill shape Karim approved for prominent
 * outbound links.
 *
 * Variants:
 *   default — translucent blue tint, blue/20 border, hover dims
 *   solid   — solid blue, hover bright + foreground/50 border
 *   ghost   — transparent, hover white/10 + zinc border
 *
 * Sizes: default / sm / lg
 *
 * The `neon` prop (default true) toggles two animated horizontal blue
 * gradient bars on the top and bottom edges of the button — they fade
 * in on hover and stay subtly visible when idle. Set `neon={false}`
 * for a plain pill.
 *
 * Source: original component supplied by Karim 2026-04-13.
 */
import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from './lib/utils';
const neonButtonVariants = cva('relative group border text-foreground mx-auto text-center rounded-full', {
    variants: {
        variant: {
            default: 'bg-blue-500/5 hover:bg-blue-500/0 border-blue-500/20',
            solid: 'bg-blue-500 hover:bg-blue-600 text-white border-transparent hover:border-foreground/50 transition-all duration-200',
            ghost: 'border-transparent bg-transparent hover:border-zinc-600 hover:bg-white/10',
        },
        size: {
            default: 'px-7 py-1.5',
            sm: 'px-4 py-0.5',
            lg: 'px-10 py-2.5',
        },
    },
    defaultVariants: {
        variant: 'default',
        size: 'default',
    },
});
const NeonButton = React.forwardRef(({ className, neon = true, size, variant, children, ...props }, ref) => {
    return (_jsxs("button", { className: cn(neonButtonVariants({ variant, size }), className), ref: ref, ...props, children: [_jsx("span", { className: cn('absolute h-px opacity-0 group-hover:opacity-100 transition-all duration-500 ease-in-out inset-x-0 inset-y-0 bg-gradient-to-r w-3/4 mx-auto from-transparent dark:via-blue-500 via-blue-600 to-transparent hidden', neon && 'block') }), children, _jsx("span", { className: cn('absolute group-hover:opacity-30 transition-all duration-500 ease-in-out inset-x-0 h-px -bottom-px bg-gradient-to-r w-3/4 mx-auto from-transparent dark:via-blue-500 via-blue-600 to-transparent hidden', neon && 'block') })] }));
});
NeonButton.displayName = 'NeonButton';
export { NeonButton, neonButtonVariants };
//# sourceMappingURL=neon-button.js.map